import { pkIsBetween } from './pkCompare';
import type { RouteRoadStretch } from '../types/surveillanceRoute';

/**
 * Espejo FRONT del plan de «Registrar parada» (spec 2026-10-06 § 3).
 *
 * 🔴 ESTE FICHERO TIENE UN ESPEJO EN EL BACK:
 * `rois-back/src/modules/surveillance-route/route-segment/segment-split-plan.ts`
 * (y `splitStretchesAt` de `road-stretches.helper.ts`). El modal pinta la vista
 * previa y los errores con esta copia ANTES de enviar; si las reglas divergen,
 * el modal enseña una tabla y el servidor guarda otra. Los casos de
 * `tests/unit/surveillanceRoute/segment-stop-plan.spec.ts` son los MISMOS, con
 * los mismos números, que los del unit del back. Si tocas una regla aquí,
 * TÓCALA ALLÍ. Y al revés.
 *
 * El modelo: un tramo es tiempo CONDUCIENDO; la parada es el hueco hasta el
 * tramo siguiente y su causa va en el tramo que acaba en ella. El original es
 * siempre la ÚLTIMA pieza (conserva id, ROID y cierre).
 */

type RoadStretchInput = RouteRoadStretch;

/** Error del plan con el código estable del backend. */
export class SplitPlanError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'SplitPlanError';
  }
}

/**
 * Parte los trozos de un tramo que cruzó de carretera por el punto de una
 * parada («Registrar parada», spec 2026-10-06 § 3).
 *
 * `before` son los trozos hasta la parada y `after` desde ella. Un lado que se
 * queda con UN solo trozo deja de cruzar: se devuelve vacío y su carretera va
 * en `beforeRoadId`/`afterRoadId`, igual que guarda `correct()` un tramo que
 * deja de cruzar (las invariantes de `resolveRoadStretches` no admiten una
 * lista de un elemento).
 *
 * La carretera de la parada la dice quien llama. Sin ella vale el único trozo
 * que contiene el PK; si lo contienen los dos —la franja que la C-25 y su
 * ramal comparten— hay que decirla: adivinar aquí falsearía los kilómetros.
 *
 * Sin trozos (tramo de una sola vía) no hay nada que partir: todo `null` y el
 * caller conserva la carretera del tramo.
 */
export const splitStretchesAt = (
  stretches: RoadStretchInput[],
  roadId: string | null | undefined,
  pk: { km: number; meter: number }
): {
  before: RoadStretchInput[];
  after: RoadStretchInput[];
  beforeRoadId: string | null;
  afterRoadId: string | null;
} => {
  if (!stretches || stretches.length === 0) {
    return { before: [], after: [], beforeRoadId: null, afterRoadId: null };
  }

  let index: number;
  if (roadId) {
    index = stretches.findIndex((s) => s.roadId === roadId);
    if (index === -1) {
      throw new SplitPlanError(
        'SR_STOP_ROAD_NOT_IN_SEGMENT',
        'La carretera de la parada no es ninguna de las que recorrió el tramo'
      );
    }
  } else {
    const containing = stretches
      .map((s, i) => ({ s, i }))
      .filter(({ s }) =>
        pkIsBetween(
          pk,
          { km: s.pkInitKm, meter: s.pkInitMeter },
          { km: s.pkEndKm, meter: s.pkEndMeter }
        )
      );
    if (containing.length !== 1) {
      throw new SplitPlanError(
        'SR_STOP_ROAD_REQUIRED',
        'Indica en qué carretera fue la parada: el tramo pasó por más de una'
      );
    }
    index = containing[0].i;
  }

  const before = stretches.slice(0, index + 1).map((s) => ({ ...s }));
  before[before.length - 1].pkEndKm = pk.km;
  before[before.length - 1].pkEndMeter = pk.meter;
  const after = stretches.slice(index).map((s) => ({ ...s }));
  after[0].pkInitKm = pk.km;
  after[0].pkInitMeter = pk.meter;

  return {
    before: before.length > 1 ? before : [],
    after: after.length > 1 ? after : [],
    beforeRoadId: before[0].roadId,
    afterRoadId: after[0].roadId,
  };
};

/**
 * Margen de borde: una parada a menos de esto del inicio o del fin del tramo
 * se trata como pegada a ese borde (recorta el tramo) en vez de crear una
 * pieza diminuta. Medido con datos reales del 5-oct: la salida de la parada
 * del T7 de Su.3802 cae 15 s antes del cierre, y la del T15 de Su.3803
 * («Arbre caigut») 74 s antes — parada, la tablet guarda UN punto por minuto,
 * así que el último fijo puede ir hasta 60 s por detrás de la salida real, y
 * después el operario escribe la descripción del cierre. Dos minutos cubren
 * las dos cosas; un trayecto de verdad tras la parada (más de 2 min) sigue
 * creando su pieza.
 */
export const STOP_EDGE_MS = 120_000;

export const SPLIT_PAUSE_CAUSES = ['basic', 'action', 'external-call'] as const;

/**
 * Minutos de una parada COMO SE VEN: de la hora a la hora, sin los segundos
 * que la pantalla no enseña (13:36:50 → 13:51:10 son 15, no 14). Es la
 * duración de la tabla del modal y la que decide si llega al minuto.
 */
export const visibleStopMinutes = (startDate: Date, endDate: Date): number =>
  Math.floor(endDate.getTime() / 60_000) -
  Math.floor(startDate.getTime() / 60_000);

export type Pk = { km: number; meter: number };

export type SplitSource = {
  startDate: Date;
  endDate: Date;
  pkInit: Pk;
  pkEnd: Pk;
  direction: string;
  directionName: string | null;
  roadId: string | null;
  roadStretches: RoadStretchInput[];
  pauseCause: string | null;
  pauseCauseDetail: string | null;
  initLatitude: number | null;
  initLongitude: number | null;
  endLatitude: number | null;
  endLongitude: number | null;
};

export type StopRequest = {
  /** Llegada: fin de la pieza anterior. */
  startDate: Date;
  /** Salida: inicio de la pieza siguiente. */
  endDate: Date;
  pk: Pk;
  roadId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  pauseCause?: string | null;
  pauseCauseDetail?: string | null;
  directionAfter?: string | null;
  directionNameAfter?: string | null;
};

export type StopShape = 'start' | 'middle' | 'end';

export type PlannedPiece = {
  isOriginal: boolean;
  startDate: Date;
  endDate: Date;
  pkInit: Pk;
  pkEnd: Pk;
  direction: string;
  directionName: string | null;
  roadId: string | null;
  roadStretches: RoadStretchInput[];
  pauseCause: string | null;
  pauseCauseDetail: string | null;
  initLatitude: number | null;
  initLongitude: number | null;
  endLatitude: number | null;
  endLongitude: number | null;
  /** Solo el original: su causa de cierre cambió (forma «final»). */
  closureChanged: boolean;
};

export type SplitPlan = {
  pieces: PlannedPiece[];
  shapes: StopShape[];
  /** El tramo empieza más tarde (forma «inicio»). */
  startMoved: boolean;
  /** El tramo acaba antes (forma «final»). */
  endMoved: boolean;
};

export function stopShape(
  segStart: Date,
  segEnd: Date,
  stop: { startDate: Date; endDate: Date },
  edgeMs: number = STOP_EDGE_MS
): StopShape | 'covers' {
  const touchesStart = stop.startDate.getTime() - segStart.getTime() < edgeMs;
  const touchesEnd = segEnd.getTime() - stop.endDate.getTime() < edgeMs;
  if (touchesStart && touchesEnd) return 'covers';
  if (touchesStart) return 'start';
  if (touchesEnd) return 'end';
  return 'middle';
}

const assertCause = (cause: string | null | undefined): void => {
  if (
    cause != null &&
    !(SPLIT_PAUSE_CAUSES as readonly string[]).includes(cause)
  ) {
    throw new SplitPlanError(
      'SR_PAUSECAUSE_INVALID',
      `Causa de parada inválida: '${cause}'. Permitidas: ${SPLIT_PAUSE_CAUSES.join(', ')}.`
    );
  }
};

/** Lo que queda del tramo por delante de la última parada aplicada. */
type Cursor = {
  startDate: Date;
  pkInit: Pk;
  direction: string;
  directionName: string | null;
  initLatitude: number | null;
  initLongitude: number | null;
  roadId: string | null;
  roadStretches: RoadStretchInput[];
};

export function planSegmentSplit(
  source: SplitSource,
  stops: StopRequest[],
  opts: { edgeMs?: number } = {}
): SplitPlan {
  const edgeMs = opts.edgeMs ?? STOP_EDGE_MS;
  if (!stops || stops.length === 0) {
    throw new SplitPlanError(
      'SR_STOP_EMPTY',
      'No hay ninguna parada que registrar'
    );
  }

  const sorted = [...stops].sort(
    (a, b) => a.startDate.getTime() - b.startDate.getTime()
  );
  sorted.forEach((stop, i) => {
    if (stop.endDate.getTime() < stop.startDate.getTime()) {
      throw new SplitPlanError(
        'SR_STOP_TIME_INVALID_RANGE',
        'La salida de la parada no puede ser anterior a la llegada'
      );
    }
    // Decisión del 15-oct (QA T1.x+d): una «parada» sin duración partía el
    // tramo en dos piezas pegadas. Al minuto que se ve en pantalla.
    if (visibleStopMinutes(stop.startDate, stop.endDate) < 1) {
      throw new SplitPlanError(
        'SR_STOP_TOO_SHORT',
        'La parada tiene que durar al menos 1 minuto'
      );
    }
    if (
      stop.startDate.getTime() < source.startDate.getTime() ||
      stop.endDate.getTime() > source.endDate.getTime()
    ) {
      throw new SplitPlanError(
        'SR_STOP_OUTSIDE_SEGMENT',
        'La parada tiene que caer dentro de las horas del tramo'
      );
    }
    // Entre dos paradas tiene que quedar al menos un minuto de tramo: pegadas
    // (una acaba cuando empieza la otra) dejaban un tramo de 0 min (29-oct).
    if (
      i > 0 &&
      stop.startDate.getTime() < sorted[i - 1].endDate.getTime() + 60_000
    ) {
      throw new SplitPlanError(
        'SR_STOP_OVERLAP',
        'Dos paradas no pueden solaparse'
      );
    }
  });

  const pieces: PlannedPiece[] = [];
  const shapes: StopShape[] = [];
  let cursor: Cursor = {
    startDate: source.startDate,
    pkInit: source.pkInit,
    direction: source.direction,
    directionName: source.directionName,
    initLatitude: source.initLatitude,
    initLongitude: source.initLongitude,
    roadId: source.roadId,
    roadStretches: source.roadStretches ?? [],
  };
  let finished = false;

  for (const stop of sorted) {
    if (finished) {
      throw new SplitPlanError(
        'SR_STOP_OVERLAP',
        'No puede haber otra parada después de la que cierra el tramo'
      );
    }
    const shape = stopShape(cursor.startDate, source.endDate, stop, edgeMs);
    if (shape === 'covers') {
      throw new SplitPlanError(
        'SR_STOP_COVERS_SEGMENT',
        'La parada ocupa todo el tramo: no queda nada que fuera conduciendo'
      );
    }
    shapes.push(shape);

    const cut = splitStretchesAt(cursor.roadStretches, stop.roadId, stop.pk);
    const beforeRoadId = cut.beforeRoadId ?? cursor.roadId;
    const afterRoadId = cut.afterRoadId ?? cursor.roadId;
    const stopLat = stop.latitude ?? null;
    const stopLng = stop.longitude ?? null;

    if (shape === 'end') {
      const cause = stop.pauseCause ?? source.pauseCause;
      assertCause(cause);
      pieces.push({
        isOriginal: true,
        startDate: cursor.startDate,
        endDate: stop.startDate,
        pkInit: cursor.pkInit,
        pkEnd: stop.pk,
        direction: cursor.direction,
        directionName: cursor.directionName,
        roadId: beforeRoadId,
        roadStretches: cut.before,
        pauseCause: cause,
        pauseCauseDetail:
          stop.pauseCauseDetail !== undefined
            ? stop.pauseCauseDetail
            : source.pauseCauseDetail,
        initLatitude: cursor.initLatitude,
        initLongitude: cursor.initLongitude,
        endLatitude: stopLat,
        endLongitude: stopLng,
        closureChanged: cause !== source.pauseCause,
      });
      finished = true;
      continue;
    }

    if (shape === 'middle') {
      if (!stop.pauseCause) {
        throw new SplitPlanError(
          'SR_STOP_CAUSE_REQUIRED',
          'Indica la causa de la parada'
        );
      }
      assertCause(stop.pauseCause);
      pieces.push({
        isOriginal: false,
        startDate: cursor.startDate,
        endDate: stop.startDate,
        pkInit: cursor.pkInit,
        pkEnd: stop.pk,
        direction: cursor.direction,
        directionName: cursor.directionName,
        roadId: beforeRoadId,
        roadStretches: cut.before,
        pauseCause: stop.pauseCause,
        pauseCauseDetail: stop.pauseCauseDetail ?? null,
        initLatitude: cursor.initLatitude,
        initLongitude: cursor.initLongitude,
        endLatitude: stopLat,
        endLongitude: stopLng,
        closureChanged: false,
      });
    }

    // «Inicio» y «medio»: lo que queda arranca a la salida de la parada.
    const turns =
      stop.directionAfter != null && stop.directionAfter !== cursor.direction;
    cursor = {
      startDate: stop.endDate,
      pkInit: stop.pk,
      direction: turns ? (stop.directionAfter as string) : cursor.direction,
      // El nombre de un sentido no vale para el contrario: sin el nuevo, vacío.
      directionName: turns
        ? (stop.directionNameAfter ?? null)
        : cursor.directionName,
      initLatitude: stopLat,
      initLongitude: stopLng,
      roadId: afterRoadId,
      roadStretches: cut.after,
    };
  }

  if (!finished) {
    pieces.push({
      isOriginal: true,
      startDate: cursor.startDate,
      endDate: source.endDate,
      pkInit: cursor.pkInit,
      pkEnd: source.pkEnd,
      direction: cursor.direction,
      directionName: cursor.directionName,
      roadId: cursor.roadId,
      roadStretches: cursor.roadStretches,
      pauseCause: source.pauseCause,
      pauseCauseDetail: source.pauseCauseDetail,
      initLatitude: cursor.initLatitude,
      initLongitude: cursor.initLongitude,
      endLatitude: source.endLatitude,
      endLongitude: source.endLongitude,
      closureChanged: false,
    });
  }

  return {
    pieces,
    shapes,
    startMoved: pieces[0].startDate.getTime() !== source.startDate.getTime(),
    endMoved:
      pieces[pieces.length - 1].endDate.getTime() !== source.endDate.getTime(),
  };
}

/**
 * Lo que usa el modal: el plan o la clave i18n del error, sin lanzar
 * (`errors:backend.<CODE>`, la misma que traduce el toast del servidor).
 */
export function tryPlanSegmentSplit(
  source: SplitSource,
  stops: StopRequest[],
  opts: { edgeMs?: number } = {}
): { plan: SplitPlan } | { error: string } {
  try {
    return { plan: planSegmentSplit(source, stops, opts) };
  } catch (error) {
    if (error instanceof SplitPlanError) {
      return { error: `errors:backend.${error.code}` };
    }
    throw error;
  }
}
